import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChangePassword } from '../../api/hooks';
import { useSessionStore } from '../../session/store';
import { ROUTES } from '../../routes/constants';

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const changePassword = useChangePassword();
  const { user, token, setSession } = useSessionStore();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError]                     = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword || !newPassword) {
      setError('Current password and new password are required.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password confirmation does not match.');
      return;
    }
    setError('');
    try {
      const profile = await changePassword.mutateAsync({
        current_password: currentPassword,
        new_password: newPassword,
      });
      if (token) {
        setSession({ user: profile, token, roles: profile.roles, mustChangePassword: false });
      }
      navigate(ROUTES.STRATEGIES, { replace: true });
    } catch (err: any) {
      setError(err?.message || 'Failed to change password.');
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <form
          onSubmit={handleSubmit}
          className="relative bg-bg-panel border border-bg-border/60 rounded-xl p-6 space-y-5 shadow-modal overflow-hidden"
        >
          {/* Top amber accent line */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber/50 to-transparent" />

          <div className="space-y-1">
            <h1 className="font-bold text-base text-txt-primary">Change your password</h1>
            <p className="text-2xs font-mono text-txt-muted">
              {user?.email || 'Your account'} must set a new password before continuing.
            </p>
          </div>

          <div className="space-y-1.5">
            <label className="text-2xs font-mono text-txt-label uppercase tracking-widest">Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              className="input-base"
              disabled={changePassword.isPending}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-2xs font-mono text-txt-label uppercase tracking-widest">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              className="input-base"
              disabled={changePassword.isPending}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-2xs font-mono text-txt-label uppercase tracking-widest">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              className="input-base"
              disabled={changePassword.isPending}
            />
          </div>

          {error && (
            <p className="text-xs text-bear bg-bear/10 border border-bear/30 rounded-lg px-3 py-2">{error}</p>
          )}

          <button type="submit" className="btn-primary w-full justify-center" disabled={changePassword.isPending}>
            {changePassword.isPending ? 'Updating…' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
